/**
 * ScheduleEditor Component
 *
 * Weekly schedule builder with a 7-column grid for Mon-Sun,
 * block management (add/edit/delete), and submit for approval.
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  ContractorSchedule,
  ScheduleBlock,
  Project,
  ScheduleBlockType,
  CreateScheduleBlockRequest,
} from '@/types/contractors';
import { DAY_NAMES, SCHEDULE_STATUS_CONFIG } from '@/types/contractors';
import { schedulesApi, scheduleBlocksApi, projectsApi } from '@/services/contractorsApi';

interface ScheduleEditorProps {
  scheduleId?: string;
  readOnly?: boolean;
}

const BLOCK_TYPE_COLORS: Record<ScheduleBlockType, string> = {
  work: 'bg-blue-100 border-blue-300 text-blue-800',
  meeting: 'bg-purple-100 border-purple-300 text-purple-800',
  admin: 'bg-yellow-100 border-yellow-300 text-yellow-800',
  other: 'bg-gray-100 border-gray-300 text-gray-800',
};

interface BlockFormData {
  day_of_week: number;
  start_time: string;
  end_time: string;
  project_id: string;
  block_type: ScheduleBlockType;
  notes: string;
}

const emptyBlockForm: BlockFormData = {
  day_of_week: 0,
  start_time: '09:00',
  end_time: '17:00',
  project_id: '',
  block_type: 'work',
  notes: '',
};

export function ScheduleEditor({ scheduleId, readOnly = false }: ScheduleEditorProps): React.JSX.Element {
  const { t } = useTranslation('contractors');

  const [schedule, setSchedule] = useState<ContractorSchedule | null>(null);
  const [blocks, setBlocks] = useState<ScheduleBlock[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Block form state
  const [showBlockForm, setShowBlockForm] = useState(false);
  const [editingBlockId, setEditingBlockId] = useState<string | null>(null);
  const [blockForm, setBlockForm] = useState<BlockFormData>(emptyBlockForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    projectsApi.list({ status: 'active', limit: 100 }).then((res) => {
      setProjects(Array.isArray(res) ? res : []);
    }).catch(() => { /* ignore */ });
  }, []);

  const fetchSchedule = useCallback(async () => {
    if (!scheduleId) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const result = await schedulesApi.get(scheduleId);
      setSchedule(result);
      setBlocks(result.blocks ?? []);
    } catch {
      setError(t('errors.loadFailed'));
    } finally {
      setIsLoading(false);
    }
  }, [scheduleId, t]);

  useEffect(() => {
    void fetchSchedule();
  }, [fetchSchedule]);

  const isEditable = !readOnly && schedule && schedule.status === 'draft';

  const handleAddBlock = (dayOfWeek: number): void => {
    setBlockForm({ ...emptyBlockForm, day_of_week: dayOfWeek });
    setEditingBlockId(null);
    setShowBlockForm(true);
  };

  const handleEditBlock = (block: ScheduleBlock): void => {
    setBlockForm({
      day_of_week: block.day_of_week,
      start_time: block.start_time,
      end_time: block.end_time,
      project_id: block.project_id ?? '',
      block_type: block.block_type,
      notes: block.notes ?? '',
    });
    setEditingBlockId(block.id);
    setShowBlockForm(true);
  };

  const handleDeleteBlock = async (blockId: string): Promise<void> => {
    if (!schedule) return;
    try {
      await scheduleBlocksApi.delete(schedule.id, blockId);
      await fetchSchedule();
    } catch {
      setError(t('errors.deleteFailed'));
    }
  };

  const handleSaveBlock = async (): Promise<void> => {
    if (!schedule) return;
    setSaving(true);
    setError(null);
    try {
      const payload: CreateScheduleBlockRequest = {
        day_of_week: blockForm.day_of_week,
        start_time: blockForm.start_time,
        end_time: blockForm.end_time,
        project_id: blockForm.project_id || undefined,
        block_type: blockForm.block_type,
        notes: blockForm.notes || undefined,
      };

      if (editingBlockId) {
        await scheduleBlocksApi.update(schedule.id, editingBlockId, payload);
      } else {
        await scheduleBlocksApi.create(schedule.id, payload);
      }
      setShowBlockForm(false);
      await fetchSchedule();
    } catch {
      setError(t('errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitForApproval = async (): Promise<void> => {
    if (!schedule) return;
    try {
      await schedulesApi.submit(schedule.id);
      await fetchSchedule();
    } catch {
      setError(t('errors.submitFailed'));
    }
  };

  if (isLoading) {
    return <div className="py-12 text-center text-sm text-gray-500">{t('common.loading')}</div>;
  }

  if (!schedule) {
    return (
      <div className="py-12 text-center text-sm text-gray-500">
        {t('schedules.noSchedule')}
      </div>
    );
  }

  const statusCfg = SCHEDULE_STATUS_CONFIG[schedule.status];

  // Group blocks by day
  const blocksByDay = new Map<number, ScheduleBlock[]>();
  for (let i = 0; i < 7; i++) blocksByDay.set(i, []);
  for (const block of blocks) {
    const dayBlocks = blocksByDay.get(block.day_of_week) ?? [];
    dayBlocks.push(block);
    blocksByDay.set(block.day_of_week, dayBlocks);
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">{schedule.name || t('schedules.untitled')}</h2>
          <div className="flex items-center gap-2 mt-1">
            <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded ${statusCfg.bgColor} ${statusCfg.color}`}>
              {t(`status.${schedule.status}`)}
            </span>
            <span className="text-xs text-gray-500">
              {schedule.effective_from} - {schedule.effective_until || t('schedules.ongoing')}
            </span>
          </div>
        </div>
        {isEditable && (
          <button
            onClick={handleSubmitForApproval}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
          >
            {t('schedules.submitForApproval')}
          </button>
        )}
      </div>

      {error && (
        <div className="p-3 text-sm text-red-700 bg-red-50 rounded-lg">{error}</div>
      )}

      {/* Week grid */}
      <div className="grid grid-cols-7 gap-2">
        {DAY_NAMES.map((day, idx) => (
          <div key={day} className="min-h-[200px]">
            <div className="text-xs font-semibold text-gray-600 uppercase text-center py-2 bg-gray-50 rounded-t border border-gray-200 border-b-0">
              {day.slice(0, 3)}
            </div>
            <div className="border border-gray-200 rounded-b p-1 space-y-1 min-h-[180px] bg-white">
              {(blocksByDay.get(idx) ?? [])
                .sort((a, b) => a.start_time.localeCompare(b.start_time))
                .map((block) => (
                  <div
                    key={block.id}
                    className={`relative p-1.5 rounded border text-xs cursor-pointer ${BLOCK_TYPE_COLORS[block.block_type]}`}
                    onClick={() => isEditable && handleEditBlock(block)}
                  >
                    <div className="font-medium">{block.start_time} - {block.end_time}</div>
                    {block.project_name && (
                      <div className="truncate opacity-80">{block.project_name}</div>
                    )}
                    <div className="capitalize opacity-70">{block.block_type}</div>
                    {isEditable && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteBlock(block.id); }}
                        className="absolute top-0.5 right-0.5 w-4 h-4 flex items-center justify-center text-gray-500 hover:text-red-600 rounded-full hover:bg-white/80"
                      >
                        x
                      </button>
                    )}
                  </div>
                ))}
              {isEditable && (
                <button
                  onClick={() => handleAddBlock(idx)}
                  className="w-full py-1.5 text-xs text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded border border-dashed border-gray-200"
                >
                  + {t('schedules.addBlock')}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Block form modal */}
      {showBlockForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingBlockId ? t('schedules.editBlock') : t('schedules.addBlock')}
              </h3>
              <button onClick={() => setShowBlockForm(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="px-6 py-4 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('schedules.day')}</label>
                <select
                  value={blockForm.day_of_week}
                  onChange={(e) => setBlockForm((f) => ({ ...f, day_of_week: Number(e.target.value) }))}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                >
                  {DAY_NAMES.map((d, i) => (
                    <option key={i} value={i}>{d}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('schedules.startTime')}</label>
                  <input
                    type="time"
                    value={blockForm.start_time}
                    onChange={(e) => setBlockForm((f) => ({ ...f, start_time: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('schedules.endTime')}</label>
                  <input
                    type="time"
                    value={blockForm.end_time}
                    onChange={(e) => setBlockForm((f) => ({ ...f, end_time: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('schedules.project')}</label>
                <select
                  value={blockForm.project_id}
                  onChange={(e) => setBlockForm((f) => ({ ...f, project_id: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                >
                  <option value="">{t('schedules.noProject')}</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('schedules.blockType')}</label>
                <select
                  value={blockForm.block_type}
                  onChange={(e) => setBlockForm((f) => ({ ...f, block_type: e.target.value as ScheduleBlockType }))}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                >
                  <option value="work">{t('schedules.types.work')}</option>
                  <option value="meeting">{t('schedules.types.meeting')}</option>
                  <option value="admin">{t('schedules.types.admin')}</option>
                  <option value="other">{t('schedules.types.other')}</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200">
              <button onClick={() => setShowBlockForm(false)} className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">
                {t('common.cancel')}
              </button>
              <button onClick={handleSaveBlock} disabled={saving} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {saving ? t('common.saving') : t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
